import {
    ConnectionQuality,
    LocalParticipant,
    Participant,
    ParticipantEvent,
    RemoteParticipant,
    Track,
    TrackPublication,
} from 'livekit-client';
import { useCallback, useEffect, useState } from 'react';
import { useRaiseHand } from 'features/RaiseHand/hooks/useRaiseHand';

export interface ParticipantState {
    isSpeaking: boolean;
    connectionQuality: ConnectionQuality;
    isLocal: boolean;
    metadata?: string;
    publications: TrackPublication[];
    subscribedTracks: TrackPublication[];
    cameraPublication?: TrackPublication;
    microphonePublication?: TrackPublication;
    screenSharePublication?: TrackPublication;
    isAudioMuted: boolean;
    isCameraOn: boolean;
}

export function useParticipant(participant: Participant | RemoteParticipant): ParticipantState {
    const getIsAudioMuted = useCallback(() => {
        const micPublication = participant.getTrackPublication(Track.Source.Microphone);
        return micPublication?.isMuted ?? !participant.isMicrophoneEnabled;
    }, [participant]);
    const [audioMuted, setAudioMuted] = useState(getIsAudioMuted);

    const getIsCameraOn = useCallback(() => {
        const camPublication = participant.getTrackPublication(Track.Source.Camera);
        return camPublication ? !camPublication.isMuted : participant.isCameraEnabled;
    }, [participant]);
    const [isCameraOn, setCameraOn] = useState(getIsCameraOn);

    const [connectionQuality, setConnectionQuality] = useState<ConnectionQuality>(participant.connectionQuality);
    const [isSpeaking, setSpeaking] = useState(false);
    const [metadata, setMetadata] = useState<string>();
    const [publications, setPublications] = useState<TrackPublication[]>([]);
    const [subscribedTracks, setSubscribedTracks] = useState<TrackPublication[]>([]);
    const { checkIsHandRaised, toggleRaiseHand } = useRaiseHand();

    const syncAudioMuted = useCallback(() => {
        setAudioMuted(getIsAudioMuted());
    }, [getIsAudioMuted]);

    const syncCameraOn = useCallback(() => {
        setCameraOn(getIsCameraOn());
    }, [getIsCameraOn]);

    const onPublicationsChanged = () => {
        const trackPublications = Array.from(participant.trackPublications.values());

        setPublications(trackPublications);
        setSubscribedTracks(
            trackPublications.filter((pub) => {
                return pub.isSubscribed && pub.track !== undefined;
            }),
        );

        syncAudioMuted();
        syncCameraOn();
    };

    useEffect(() => {
        const onMuted = (pub: TrackPublication) => {
            if (pub.kind === Track.Kind.Audio) {
                syncAudioMuted();
            } else if (pub.kind === Track.Kind.Video) {
                syncCameraOn();
            }
        };
        const onUnmuted = (pub: TrackPublication) => {
            if (pub.kind === Track.Kind.Audio) {
                syncAudioMuted();
            } else if (pub.kind === Track.Kind.Video) {
                syncCameraOn();
            }
        };
        const onMetadataChanged = () => {
            if (participant.metadata) {
                setMetadata(participant.metadata);
            }
        };
        const onIsSpeakingChanged = () => {
            const isHandRaised = checkIsHandRaised(participant);
            const isLocalParticipant = participant.isLocal;
            const isSpeaking = participant.isSpeaking;

            setSpeaking(isSpeaking);

            if (isHandRaised && isLocalParticipant && isSpeaking) {
                toggleRaiseHand(participant as LocalParticipant);
            }
        };
        const onConnectionQualityUpdate = (quality: ConnectionQuality) => {
            setConnectionQuality(quality);
        };
        const onPermissionsChanged = () => {
            syncAudioMuted();
            syncCameraOn();
        };

        // register listeners
        participant
            .on(ParticipantEvent.TrackMuted, onMuted)
            .on(ParticipantEvent.TrackUnmuted, onUnmuted)
            .on(ParticipantEvent.ParticipantMetadataChanged, onMetadataChanged)
            .on(ParticipantEvent.IsSpeakingChanged, onIsSpeakingChanged)
            .on(ParticipantEvent.TrackPublished, onPublicationsChanged)
            .on(ParticipantEvent.TrackUnpublished, onPublicationsChanged)
            .on(ParticipantEvent.TrackSubscribed, onPublicationsChanged)
            .on(ParticipantEvent.TrackUnsubscribed, onPublicationsChanged)
            .on(ParticipantEvent.LocalTrackPublished, onPublicationsChanged)
            .on(ParticipantEvent.LocalTrackUnpublished, onPublicationsChanged)
            .on(ParticipantEvent.ConnectionQualityChanged, onConnectionQualityUpdate)
            .on(ParticipantEvent.ParticipantPermissionsChanged, onPermissionsChanged);

        // set initial state
        onMetadataChanged();
        onIsSpeakingChanged();
        onPublicationsChanged();

        return () => {
            // cleanup
            participant
                .off(ParticipantEvent.TrackMuted, onMuted)
                .off(ParticipantEvent.TrackUnmuted, onUnmuted)
                .off(ParticipantEvent.ParticipantMetadataChanged, onMetadataChanged)
                .off(ParticipantEvent.IsSpeakingChanged, onIsSpeakingChanged)
                .off(ParticipantEvent.TrackPublished, onPublicationsChanged)
                .off(ParticipantEvent.TrackUnpublished, onPublicationsChanged)
                .off(ParticipantEvent.TrackSubscribed, onPublicationsChanged)
                .off(ParticipantEvent.TrackUnsubscribed, onPublicationsChanged)
                .off(ParticipantEvent.LocalTrackPublished, onPublicationsChanged)
                .off(ParticipantEvent.LocalTrackUnpublished, onPublicationsChanged)
                .off(ParticipantEvent.ConnectionQualityChanged, onConnectionQualityUpdate)
                .off(ParticipantEvent.ParticipantPermissionsChanged, onPermissionsChanged);
        };
    }, [participant, syncAudioMuted, syncCameraOn]);

    // let muted: boolean | undefined;
    // participant.audioTracks.forEach((pub) => {
    //   muted = pub.isMuted;
    // });
    // if (muted === undefined) {
    //   muted = true;
    // }
    // if (isAudioMuted !== muted) {
    //   setAudioMuted(muted);
    // }

    return {
        isLocal: participant instanceof LocalParticipant,
        isSpeaking,
        connectionQuality,
        publications,
        subscribedTracks,
        cameraPublication: participant.getTrackPublication(Track.Source.Camera),
        microphonePublication: participant.getTrackPublication(Track.Source.Microphone),
        screenSharePublication: participant.getTrackPublication(Track.Source.ScreenShare),
        metadata,
        isAudioMuted: audioMuted,
        isCameraOn,
    };
}
